import * as React from "react"
import { Drawer as DrawerPrimitive } from "vaul"

import { cn } from "@/lib/utils"

function Drawer({
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Root>) {
  return <DrawerPrimitive.Root data-slot="drawer" {...props} />
}

function DrawerTrigger({
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Trigger>) {
  return <DrawerPrimitive.Trigger data-slot="drawer-trigger" {...props} />
}

function DrawerPortal({
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Portal>) {
  return <DrawerPrimitive.Portal data-slot="drawer-portal" {...props} />
}

function DrawerClose({
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Close>) {
  return <DrawerPrimitive.Close data-slot="drawer-close" {...props} />
}

function DrawerOverlay({
  className,
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Overlay>) {
  return (
    <DrawerPrimitive.Overlay
      data-slot="drawer-overlay"
      className={cn(
        "fixed inset-0 z-layer-overlay bg-[var(--modal-overlay)] data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0",
        className
      )}
      {...props}
    />
  )
}

/**
 * A side drawer's width, when the caller needs one other than the default.
 *
 * WHY THIS IS A PROP AND NOT JUST A CLASS (added 2026-08-11). The side defaults below are written as
 * `data-[vaul-drawer-direction=right]:w-3/4` and `data-[vaul-drawer-direction=right]:sm:max-w-sm`,
 * which compile to a class + attribute selector at specificity (0,2,0). A call site passing plain
 * `sm:w-[46rem] sm:max-w-none` through `className` is at (0,1,0), so the base wins **whatever the
 * emission order**, and twMerge cannot help: the data-attribute variant is part of its conflict key,
 * so it keeps both classes rather than dropping the loser.
 *
 * The result is silent and total — the caller's width is applied and then clamped, so the drawer
 * renders at the default and nothing errors. The messages drawer shipped a five-step width ladder up
 * to 46rem on 2026-08-11 and rendered 384px in every state; found by the ux-refine run (finding
 * `chat-drawer-width-clamped-to-384px`), measured identically across three frames and both themes.
 *
 * So a width passed here REPLACES the side defaults rather than competing with them. Same resolution
 * the design language already records for `SimpleDialog`, which moved width to a prop for this exact
 * trap. Callers keep using ordinary responsive utilities; they simply arrive somewhere nothing
 * outranks them.
 */
function DrawerContent({
  className,
  children,
  widthClassName,
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Content> & { widthClassName?: string }) {
  // When the caller declares a width, the base must withhold BOTH of its width utilities, not just
  // the max-width: dropping only `sm:max-w-sm` would leave `w-3/4` (also (0,2,0)) to beat the
  // caller's `sm:w-[46rem]`, and the drawer would render at three-quarters of the viewport instead.
  const sideWidthDefaults = widthClassName
    ? ""
    : "data-[vaul-drawer-direction=right]:w-3/4 data-[vaul-drawer-direction=right]:sm:max-w-sm "
      + "data-[vaul-drawer-direction=left]:w-3/4 data-[vaul-drawer-direction=left]:sm:max-w-sm"
  return (
    <DrawerPortal data-slot="drawer-portal">
      <DrawerOverlay />
      <DrawerPrimitive.Content
        data-slot="drawer-content"
        className={cn(
          // `outline-none` is the whole of ux-critique NOTIFICATIONS-24, and it is a CONSISTENCY fix
          // rather than a new decision: `dialog.tsx`'s DialogContent already carries it, on the same
          // kind of surface with the same programmatic-focus-on-open behaviour. vaul focuses the
          // content element when the drawer opens and nothing here suppressed or restyled the user
          // agent's outline, so what painted was the browser's — sampled (0,95,204) desktop light,
          // (153,200,255) desktop dark, and #E59700 on Android in BOTH themes: a warm amber
          // full-perimeter frame, in a colour that appears nowhere in this product's stylesheet,
          // around a cool near-black panel. A focus indicator that changes colour by platform is the
          // one cue that is supposed to be identical everywhere.
          //
          // Do NOT also remove the focus move: focusing the panel on open is what makes the drawer
          // keyboard-usable. This container is a dialog ROOT rather than a control, so the ring
          // belongs to the first real control inside it, which is where it already is.
          "group/drawer-content fixed z-layer-overlay-front flex h-auto flex-col border-border/70 bg-popover shadow-[var(--surface-shadow-lg)] outline-none",
          "data-[vaul-drawer-direction=top]:inset-x-0 data-[vaul-drawer-direction=top]:top-0 data-[vaul-drawer-direction=top]:mb-24 data-[vaul-drawer-direction=top]:max-h-[calc(var(--seavien-app-viewport-height)-2rem)] data-[vaul-drawer-direction=top]:rounded-b-lg data-[vaul-drawer-direction=top]:border-b",
          "data-[vaul-drawer-direction=bottom]:inset-x-0 data-[vaul-drawer-direction=bottom]:bottom-0 data-[vaul-drawer-direction=bottom]:mt-24 data-[vaul-drawer-direction=bottom]:max-h-[calc(var(--seavien-app-viewport-height)-2rem)] data-[vaul-drawer-direction=bottom]:rounded-t-lg data-[vaul-drawer-direction=bottom]:border-t",
          "data-[vaul-drawer-direction=right]:top-0 data-[vaul-drawer-direction=right]:right-0 data-[vaul-drawer-direction=right]:h-(--seavien-app-viewport-height) data-[vaul-drawer-direction=right]:max-h-(--seavien-app-viewport-height) data-[vaul-drawer-direction=right]:border-l",
          "data-[vaul-drawer-direction=left]:top-0 data-[vaul-drawer-direction=left]:left-0 data-[vaul-drawer-direction=left]:h-(--seavien-app-viewport-height) data-[vaul-drawer-direction=left]:max-h-(--seavien-app-viewport-height) data-[vaul-drawer-direction=left]:border-r",
          sideWidthDefaults,
          widthClassName,
          className
        )}
        {...props}
      >
        <div className="mx-auto mt-4 hidden h-2 w-[100px] shrink-0 rounded-full bg-muted group-data-[vaul-drawer-direction=bottom]/drawer-content:block" />
        {children}
      </DrawerPrimitive.Content>
    </DrawerPortal>
  )
}

function DrawerHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="drawer-header"
      className={cn(
        "flex flex-col gap-0.5 p-4 group-data-[vaul-drawer-direction=bottom]/drawer-content:text-center group-data-[vaul-drawer-direction=top]/drawer-content:text-center md:gap-1.5 md:text-left",
        className
      )}
      {...props}
    />
  )
}

function DrawerFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="drawer-footer"
      className={cn("mt-auto flex flex-col gap-2 p-4", className)}
      {...props}
    />
  )
}

function DrawerTitle({
  className,
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Title>) {
  return (
    <DrawerPrimitive.Title
      data-slot="drawer-title"
      className={cn("font-semibold text-foreground", className)}
      {...props}
    />
  )
}

function DrawerDescription({
  className,
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Description>) {
  return (
    <DrawerPrimitive.Description
      data-slot="drawer-description"
      className={cn("text-sm text-muted-foreground", className)}
      {...props}
    />
  )
}

export {
  Drawer,
  DrawerPortal,
  DrawerOverlay,
  DrawerTrigger,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerFooter,
  DrawerTitle,
  DrawerDescription,
}
